import React, {useState} from 'react';
import {Grid, Box, Typography} from '@mui/material';
import {t} from 'i18next';
import './QueryResults.scss';
import ArrResultTile, {
  ArrResultTileType,
} from '../../../common/arr-result-tile/ArrResultTile';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../shared/constants';
import {ResultType, SessionSearch} from '../../types/SessionSearch';
import {
  useRunConceptualSearch,
  useGetConceptualSearchStatus,
} from '../../../../hooks/session-search/useSessionSearch';
import {AddToShoppingCart} from '../../../../redux/slice/sessionSearchSlice';
import {useDispatch} from 'react-redux';

export interface QueryResultsProps {
  item: SessionSearch;
  OnAddToShoppingCart?: (type: ResultType) => boolean;
}

const QueryResults: React.FC<QueryResultsProps> = ({
  item,
  OnAddToShoppingCart,
}) => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<ToastType>(ToastType.Success);

  const [conceptualValue, setConceptualValue] = useState('');
  const [conceptualIsPending, setConceptualIsPending] = useState(false);
  const {mutate: runConceptualSearch} = useRunConceptualSearch();
  const {mutate: getConceptualSearchStatus} = useGetConceptualSearchStatus();
  const dispatch = useDispatch();

  const hasCombinedResults =
    item.familyMemberDocuments ||
    item.nearDuplicateDocuments ||
    item.threadedEmailDocuments;

  const isDraftWithoutResults =
    !!item.isSaved && !item.searchResult && !item.searchIsInProgress;

  const renderSubHeader = () => {
    let subHeader = '';

    if (hasCombinedResults) {
      subHeader = t('sessionSearch.queryResults.combined.heading.subHeader');
    } else {
      subHeader = t('sessionSearch.queryResults.standard.heading.subHeader');
    }

    return subHeader;
  };

  const renderMatchedDocs = () => {
    let caption = '';

    let type: ArrResultTileType;
    const isInactive = item.searchResult?.pureHit?.addedToShoppingCart === true;

    if (item.searchIsInProgress) {
      if (item.searchResult?.pureHitStatus === 'INPROGRESS') {
        type = 'loading';
      } else {
        type = isInactive ? 'inactive' : 'not-selected';
      }
    } else {
      type = isInactive ? 'inactive' : 'not-selected';
    }

    if (hasCombinedResults) {
      caption = t('sessionSearch.queryResults.combined.results.matchedDocs');
    } else {
      caption = t('sessionSearch.queryResults.standard.results.matchedDocs');
    }

    return (
      <ArrResultTile
        color={item.color}
        value={item.searchResult?.pureHit?.pureHitCount}
        caption={caption}
        toolTipText={t(
          'sessionSearch.queryResults.standard.results.matchedDocsTooltip'
        )}
        dataTestId={`results-matched-docs-${item.id}`}
        type={type}
        onSelect={() => {
          if (OnAddToShoppingCart && OnAddToShoppingCart(ResultType.PureHit)) {
            dispatch(
              AddToShoppingCart({
                id: item.id,
                sourceID: item.advancedSearchId,
                type: ResultType.PureHit,
                color: item.color,
                count: item.searchResult?.pureHit?.pureHitCount,
                SearchHitDocuments: item.searchResult?.pureHit?.pureHitData,
                caption: caption,
              })
            );
          }
        }}
      />
    );
  };

  const renderThreadedDocs = () => {
    const isInactive =
      item.searchResult?.emailThread?.addedToShoppingCart === true;

    // Default values
    let type: ArrResultTileType = 'loading';
    let caption = t('sessionSearch.queryResults.standard.results.threadedDocs');

    // 1. Combined mode takes priority (unless inactive)
    if (item.threadedEmailDocuments) {
      type = isInactive ? 'inactive' : 'combined';
      caption = t('sessionSearch.queryResults.combined.results.threadedDocs');
    } else {
      // 2. Standard mode
      if (item.searchIsInProgress) {
        if (item.searchResult?.emailThreadStatus === 'INPROGRESS') {
          type = 'loading';
        } else {
          type = isInactive ? 'inactive' : 'not-selected';
        }
      } else {
        type = isInactive ? 'inactive' : 'not-selected';
      }
    }

    return (
      <ArrResultTile
        value={item.searchResult?.emailThread?.emailThreadCount}
        color={item.color}
        caption={caption}
        toolTipText={t(
          'sessionSearch.queryResults.standard.results.threadedDocsTooltip'
        )}
        dataTestId={`results-threaded-docs-${item.id}`}
        type={type}
        onSelect={() => {
          if (
            OnAddToShoppingCart &&
            OnAddToShoppingCart(ResultType.EmailThread)
          ) {
            dispatch(
              AddToShoppingCart({
                id: item.id,
                sourceID: item.advancedSearchId,
                type: ResultType.EmailThread,
                color: item.color,
                count: item.searchResult?.emailThread?.emailThreadCount,
                caption,
                SearchHitDocuments:
                  item.searchResult?.emailThread?.emailThreadData,
              })
            );
          }
        }}
      />
    );
  };

  const renderNearDupeDocs = () => {
    const isInactive =
      item.searchResult?.nearDuplicate?.addedToShoppingCart === true;

    // Default values
    let type: ArrResultTileType = 'loading';
    let caption = t('sessionSearch.queryResults.standard.results.nearDupeDocs');

    // 1. Combined mode takes priority (unless inactive)
    if (item.nearDuplicateDocuments) {
      type = isInactive ? 'inactive' : 'combined';
      caption = t('sessionSearch.queryResults.combined.results.nearDupeDocs');
    } else {
      // 2. Standard mode
      if (item.searchIsInProgress) {
        if (item.searchResult?.nearDuplicateStatus === 'INPROGRESS') {
          type = 'loading';
        } else {
          type = isInactive ? 'inactive' : 'not-selected';
        }
      } else {
        type = isInactive ? 'inactive' : 'not-selected';
      }
    }

    return (
      <ArrResultTile
        color={item.color}
        value={item.searchResult?.nearDuplicate?.nearDuplicateCount}
        caption={caption}
        toolTipText={t(
          'sessionSearch.queryResults.standard.results.nearDupeDocsTooltip'
        )}
        dataTestId={`results-near-dupes-${item.id}`}
        type={type}
        onSelect={() => {
          if (OnAddToShoppingCart && OnAddToShoppingCart(ResultType.NearDupe)) {
            dispatch(
              AddToShoppingCart({
                id: item.id,
                sourceID: item.advancedSearchId,
                type: ResultType.NearDupe,
                color: item.color,
                count: item.searchResult?.nearDuplicate?.nearDuplicateCount,
                caption,
                SearchHitDocuments:
                  item.searchResult?.nearDuplicate?.nearDuplicateData,
              })
            );
          }
        }}
      />
    );
  };

  const renderRelatedDocs = () => {
    const isInactive =
      item.searchResult?.familyMember?.addedToShoppingCart === true;

    // Default values
    let type: ArrResultTileType = 'not-selected';
    let caption = t('sessionSearch.queryResults.standard.results.relatedDocs');

    // 1. Combined mode takes priority (unless inactive)
    if (item.familyMemberDocuments) {
      type = isInactive ? 'inactive' : 'combined';
      caption = t('sessionSearch.queryResults.combined.results.relatedDocs');
    } else {
      // 2. Standard mode
      if (item.searchIsInProgress) {
        if (item.searchResult?.familyMemberStatus === 'INPROGRESS') {
          type = 'loading';
        } else {
          type = isInactive ? 'inactive' : 'not-selected';
        }
      } else {
        type = isInactive ? 'inactive' : 'not-selected';
      }
    }

    return (
      <ArrResultTile
        color={item.color}
        value={item.searchResult?.familyMember?.familyMemberCount}
        caption={caption}
        toolTipText={t(
          'sessionSearch.queryResults.standard.results.relatedDocsTooltip'
        )}
        dataTestId={`results-related-docs-${item.id}`}
        type={type}
        onSelect={() => {
          if (
            OnAddToShoppingCart &&
            OnAddToShoppingCart(ResultType.FamilyMember)
          ) {
            dispatch(
              AddToShoppingCart({
                id: item.id,
                sourceID: item.advancedSearchId,
                type: ResultType.FamilyMember,
                color: item.color,
                count: item.searchResult?.familyMember?.familyMemberCount,
                caption,
                SearchHitDocuments:
                  item.searchResult?.familyMember?.familyMemberData,
              })
            );
          }
        }}
      />
    );
  };

  const getConceptualTileType = (): ArrResultTileType => {
    let tileType: ArrResultTileType = 'not-selected';
    if (item?.searchResult?.ConceptualResultInShoppingCart)
      tileType = 'inactive';
    else if (conceptualValue) {
      // If there is a value, it has been executed and should be selectable
      tileType = 'not-selected';
    } else if (conceptualIsPending) {
      // If it is pending, it is loading and should show a spinner
      tileType = 'loading';
    } else {
      // If it does not have a value and is not pending, it should be executable
      tileType = 'play';
    }

    return tileType;
  };

  const handleOnRun = () => {
    setConceptualIsPending(true);
    runConceptualSearch(item.advancedSearchId!, {
      onSuccess: (data) => {
        setToastType(ToastType.Success);
        setToastMessage(data.message ?? '');
        pollForRunStatus();
      },
      onError: () => {
        setToastType(ToastType.Error);
        setToastMessage(t('sessionSearch.unexpectedError'));
        setConceptualIsPending(false);
      },
      onSettled: () => {
        setShowToast(true);
      },
    });
  };

  const pollForRunStatus = () => {
    const pollId = window.setInterval(() => {
      getConceptualSearchStatus(item.advancedSearchId!, {
        onSuccess: (data) => {
          if (data.status === 'COMPLETE') {
            setConceptualIsPending(false);
            setConceptualValue(data.cnt);
            window.clearInterval(pollId);
          }
        },
        onError: () => {
          setConceptualIsPending(false);
          setToastType(ToastType.Error);
          setToastMessage(t('sessionSearch.unexpectedError'));
          setShowToast(true);
          window.clearInterval(pollId);
        },
      });
    }, 1000);
  };

  if (isDraftWithoutResults) {
    return (
      <Box className="search-results">
        <Typography
          className="draft-text"
          data-testid="view-session-search-draft-state"
        >
          {t('sessionSearch.draftState')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="search-results">
      {/* Header */}
      <Box className="search-results__heading">
        <Typography
          className="typo-text subhead3_size search-results__header"
          data-testid={`query-results-header`}
          tabIndex={0}
        >
          {t('sessionSearch.queryResults.standard.heading.header')}
        </Typography>
        <Typography
          className="typo-text body5_size search-results__sub-header"
          data-testid={`query-results-subheader`}
          tabIndex={0}
        >
          {renderSubHeader()}
        </Typography>
      </Box>
      {/* Results */}
      <Grid container spacing={2} className="search-results__content">
        <Grid>{renderMatchedDocs()}</Grid>
        <Grid>{renderThreadedDocs()}</Grid>
        <Grid>{renderNearDupeDocs()}</Grid>
        <Grid>{renderRelatedDocs()}</Grid>
        <Grid>
          <ArrResultTile
            value={conceptualValue}
            color={item.color}
            caption={t(
              'sessionSearch.queryResults.standard.results.similarDocs'
            )}
            toolTipText={t(
              'sessionSearch.queryResults.standard.results.similarDocsTooltip'
            )}
            dataTestId={`results-similar-docs-${item.id}`}
            type={getConceptualTileType()}
            onRun={handleOnRun}
            onSelect={() => {
              if (
                OnAddToShoppingCart &&
                OnAddToShoppingCart(ResultType.Conceptual)
              ) {
                dispatch(
                  AddToShoppingCart({
                    id: item.id,
                    sourceID: item.advancedSearchId,
                    type: ResultType.Conceptual,
                    color: item.color,
                    count: conceptualValue,
                    caption: t(
                      'sessionSearch.queryResults.standard.results.similarDocs'
                    ),
                  })
                );
              }
            }}
          />
        </Grid>
      </Grid>
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={[toastType]}
          onClose={() => setShowToast(false)}
        />
      )}
    </Box>
  );
};

export default QueryResults;
